import type { ItemFieldRow } from './item-field-reader';
import { createInventoryReadContext, isThinnedInventoryRead } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { phpJsonInt } from './php-json-values';
import { readCreatureStatuses, AuraOwnerGateUnavailable, ownerGatedAuraLivePayload } from './creature-status-reader';

export interface LayerValueDependencies {
    inventoryContext?: InventoryReadContext;
    nodeItemId: (row: ItemFieldRow) => string | null;
    providers: (row: ItemFieldRow) => Iterable<ItemFieldRow>;
    /** Exact previously resolved _ft_resolve_inventory_instance_full result, including known null.
     * A template definition cannot replace this per-instance compile. */
    resolveInventoryInstance?: (id: string) => ItemFieldRow | null;
}
export class MissingLayerValueInventory extends Error {
    constructor(public readonly id: string) { super(`Missing inventory compile for layer_value: ${id}`); this.name = 'MissingLayerValueInventory'; }
}
function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function has(value: unknown, key: string): boolean { return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key); }
function own(value: unknown, key: string): unknown { return has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined; }
function path(value: unknown, ...keys: string[]): unknown { for (const key of keys) value = own(value, key); return value; }
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function total(value: unknown): unknown { return arrayLike(value) ? own(value, 'total') ?? null : value; }
function collapse(value: unknown): unknown { return has(value, '0') ? own(value, '0') : value; }
const senses = new Set(['nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense']);
const castOverrides = new Set(['range', 'aoe_distance', 'aoe_distance_height', 'aoe_distance_depth', 'damage_type', 'target_opposing_team_only']);
const acMap: Readonly<Record<string, string>> = { max_body_ac_mod: 'max_body', natural_armor_ac_mod: 'natural_armor', armor_ac_mod: 'armor',
    shield_ac_mod: 'shield', total_ac_mod: 'total_mod', dr_total: 'dr', equipped_armor_inventory_item_id: 'equipped_armor_id',
    equipped_shield_inventory_item_id: 'equipped_shield_id', ec_total: 'ec', ac_total: 'ac', max_body_ac_mod_total: 'max_body',
    natural_armor_ac_mod_total: 'natural_armor', armor_ac_mod_total: 'armor', shield_ac_mod_total: 'shield', total_ac_mod_total: 'total_mod' };
const clusterMap: Readonly<Record<string, readonly [string, string]>> = {
    creature_encumbered_carrying_capacity: ['capacity', 'encumbered'], creature_heavily_encumbered_carrying_capacity: ['capacity', 'heavily_encumbered'],
    creature_max_carrying_capacity: ['capacity', 'max'], creature_push_drag_lift_capacity: ['capacity', 'push_drag_lift'],
    creature_base_size: ['size', 'base'], creature_size_weight_multiplier: ['size', 'weight_multiplier'], creature_physics_height: ['size', 'height'],
    hunger_meter_max: ['meters', 'hunger_max'], thirst_meter_max: ['meters', 'thirst_max'], extra_attack: ['combat', 'extra_attack'],
    hindered_attack_counter: ['combat', 'hindered_attack_counter'], total_level: ['meta', 'total_level'], max_summons: ['meta', 'max_summons'],
    body_vanishes_at_zero_vitality: ['meta', 'body_vanishes_at_zero_vitality'], arcane_threshold: ['spellcasting', 'arcane_threshold'],
    spellcasting_essence_mod: ['spellcasting', 'essence_mod'],
};
const equippedKeys = new Set(['count_main_hand_equipped', 'count_off_hand_equipped', 'count_both_hands_equipped', 'count_main_hand_weapon_equipped',
    'count_off_hand_weapon_equipped', 'count_both_hands_weapon_equipped', 'count_head_weapon_equipped', 'count_armor_equipped', 'count_shield_equipped',
    'count_main_hand_natural_weapon_equipped', 'count_off_hand_natural_weapon_equipped', 'equipped_main_hand_natural_weapon_item_id',
    'equipped_off_hand_natural_weapon_item_id', 'equipped_armor_inventory_id', 'equipped_shield_inventory_id']);
const campaignKeys = new Set(['campaign_level', 'current_session_number', 'campaign_session_price_f', 'calendar_current_date_f',
    'calendar_current_time_f', 'calendar_current_full_date_f', 'calendar_current_full_date_named_f']);

/** Whole scalar reader with request-owned inventory bridge guards and per-path provider cycle guards.
 * The supplied provider context and full-instance resolver retain their own request memo contracts.
 */
export function createLayerValueReader(dependencies: LayerValueDependencies): {
    read: (row: ItemFieldRow | null, name: string, visited?: ItemFieldRow) => unknown;
} {
    const inventory = dependencies.inventoryContext ?? createInventoryReadContext(id => {
        if (!dependencies.resolveInventoryInstance) throw new MissingLayerValueInventory(id);
        return dependencies.resolveInventoryInstance(id);
    });
    function read(row: ItemFieldRow | null, name: string, prior: ReadonlySet<string>): unknown {
        if (row === null) return null;
        const visited = new Set(prior), self = dependencies.nodeItemId(row);
        if (self !== null) visited.add(self);
        // R-116 part 2 (2026-09-30): an owner-gated aura refuses only a read its live state can move (creature-status-reader.ts)
        for (const status of Object.values(readCreatureStatuses(row))) {
            if (ownerGatedAuraLivePayload(status, name)) throw new AuraOwnerGateUnavailable('aura-owner-live-payload-rebuild');
        }
        const computed = own(row, 'computed'), creature = own(computed, 'creature');
        if (['body', 'mind', 'soul', 'spirit', 'fortune'].includes(name)) {
            const value = path(creature, 'essences', name, 'total') ?? path(computed, 'essences', name, 'total');
            if (value != null) return phpJsonInt(value);
        }
        if (senses.has(name)) { const value = path(creature, 'senses', name); if (value != null) return total(value); }
        if (name === 'teleport' || name.startsWith('speed_')) {
            const value = path(creature, 'speeds', name === 'teleport' ? name : name.slice(6));
            if (value != null) return total(value);
        }
        if (name === 'size') { const value = path(creature, 'size', 'breakdown', 'final_size_id'); if (value != null && value !== '') return value; }
        if (name === 'reach') { const value = own(creature, 'reach'); if (value != null) return total(value); }
        if (castOverrides.has(name)) { const value = own(computed, name); if (value != null && value !== '') return total(value); }
        if (name === 'extra_attack') { const value = path(creature, 'attacks', 'extra_attack'); if (value != null) return total(value); }
        if (name === 'damage_mitigation') {
            const value = path(creature, 'damage_mitigation', 'total') ?? path(creature, 'ac', 'mitigation', 'total');
            if (value != null) return phpJsonInt(value);
        }
        if (name === 'force_move_resistance' || name === 'immune_to_forced_movement') {
            const value = path(creature, 'defenses', name);
            if (arrayLike(value) && Object.keys(value).length > 0) return 1;
        }
        const acKey = own(acMap, name);
        if (typeof acKey === 'string') { const value = path(creature, 'ac', acKey); if (value != null) return total(value); }
        if (name === 'spellcasting_essence') {
            const value = path(creature, 'spellcasting', 'essence') ?? path(creature, 'spellcasting', 'essence_id');
            if (value != null) return arrayLike(value) ? path(value, 'items', 'item_id') ?? own(value, 'total') ?? null : value;
        }
        if (name === 'main_weapon_damage_type') {
            const value = path(creature, 'combat', name);
            if (value != null) return arrayLike(value) ? path(value, 'items', 'item_id') ?? null : value;
        }
        const modifier = path(creature, 'modifiers', name);
        if (modifier != null) return total(modifier);
        if (has(clusterMap, name)) { const value = path(creature, ...clusterMap[name]!); if (value != null) return total(value); }
        if (equippedKeys.has(name)) { const value = path(creature, 'equipped', name); if (value != null) return value; }
        if (campaignKeys.has(name) && has(own(computed, 'campaign'), name)) return collapse(path(computed, 'campaign', name)) ?? null;
        if (has(own(computed, 'encounter'), name)) return collapse(path(computed, 'encounter', name)) ?? null;
        let found = false, value: unknown = null;
        for (const bucket of ['locations', 'campaigns', 'encounters', 'item_layer_types']) {
            if (has(own(row, bucket), name)) { found = true; value = path(row, bucket, name); break; }
        }
        if (!found && has(row, name)) { found = true; value = own(row, name); }
        const empty = arrayLike(value) && Object.keys(value).length === 0;
        if (!found || value == null || empty) {
            if (isThinnedInventoryRead(row)) {
                const answer = inventory.read(row, full => read(full, name, visited));
                if (answer != null) return answer;
            }
            for (const carrier of dependencies.providers(row)) {
                const id = dependencies.nodeItemId(carrier);
                if (id !== null && visited.has(id)) continue;
                const answer = read(carrier, name, visited);
                if (answer != null) return answer;
            }
            if (!found || empty) return null;
        }
        value = collapse(value);
        const bucketId = path(value, 'items', 'item_id');
        if (typeof bucketId === 'string') return bucketId;
        const legacyId = own(value, 'item_id');
        return typeof legacyId === 'string' ? legacyId : value ?? null;
    }
    return { read: (row, name, visited = {}) => clone(read(row, name,
        new Set(Object.entries(visited).filter(([, value]) => value != null).map(([key]) => key)))) };
}
